import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { Col, Row } from "antd";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { getSecretsListByCurrentUserName } from "../api's/ConnectorsApi";

import ADLSDatalakeIcon from "../assets/images/Azure-DataLake-icon.png";
import PostgresqlIcon from "../assets/images/readPostgres.png";
import S3DatalakeIcon from "../assets/images/s3.png";
import oracleIcon from "../assets/images/oracle.png";
import snowflakeIcon from "../assets/images/snowFlake.png";
import sqlServerIcon from "../assets/images/sqlServer.png";

import ClientNoData from "../components/Client/ClientNoData";
import PostgreSQLConnectionForm from "../components/connectors/PostgreSQLConnectionForm";
import S3DataLakeForm from "../components/connectors/S3DataLakeForm";
import SecretTable from "../components/tables/SecretTable";
import LoadingIcon from "../reusable-component/LoadingIcon";
import { CONNECTOR_PAGE } from "../utils/commonConstants";
import SnowflakeConnectorForm from "../components/connectors/SnowflakeConnectorForm";
import SQLServerConnectorForm from "../components/connectors/SQLServerConnectorForm";
import OracleDBConnectorForm from "../components/connectors/OracleDBConnectorForm";
import ADLSDatalakeConnectorForm from "../components/connectors/ADLSDatalakeConnectorForm";

const serviceList = [
  { name: "ADLS Datalake", icon: ADLSDatalakeIcon, type: "Datalake" },
  { name: "OracleDB", icon: oracleIcon, type: "Database" },
  { name: "PostgreSQL", icon: PostgresqlIcon, type: "Database" },
  { name: "Snowflake", icon: snowflakeIcon, type: "Data Warehouse" },
  { name: "SQL Server", icon: sqlServerIcon, type: "Database" },
  { name: "S3 Datalake", icon: S3DatalakeIcon, type: "Datalake" },
];

const filters = ["All", "Database", "Datalake", "Data Warehouse"];

const ConnectorsPage = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState(0);

  const [secrets, setSecrets] = useState([]);
  const [totalData, setTotalData] = useState(0);

  const [page, setPage] = useState(1); // MUI Pagination is 1-based
  const size = 5;

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState(["All"]);

  const filteredServices = useMemo(() => {
    if (selectedFilters.includes("All")) return serviceList;
    return serviceList.filter((service) =>
      selectedFilters.includes(service.type)
    );
  }, [selectedFilters]);

  const handleFilterClick = (filterType) => {
    if (filterType === "All") {
      setSelectedFilters(["All"]);
    } else {
      setSelectedFilters((prev) => {
        if (prev.includes("All")) return [filterType];
        if (prev.includes(filterType)) {
          const updated = prev.filter((f) => f !== filterType);
          return updated.length ? updated : ["All"];
        }
        return [...prev, filterType];
      });
    }
  };

  const handleChange = (_event, newValue) => setValue(newValue);

  const handleCardClick = (service) => {
    setSelectedService(service);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const getSecrets = async (p = page, s = search) => {
    if (value !== 1) return;

    try {
      setLoading(true);

      const res = await getSecretsListByCurrentUserName(
        p - 1,
        size,
        s.trim() || null
      );

      if (res.status === 200) {
        setSecrets(res.data.data);
        setTotalData(res.data.totalData);
      }
    } catch (err) {
      enqueueSnackbar("Unable to fetch secrets!", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (value !== 1) return;
    const delay = setTimeout(() => {
      getSecrets(1, search);
      setPage(1);
    }, 500);

    return () => clearTimeout(delay);
  }, [search, value]);

  const handlePageChange = (_e, newPage) => {
    setPage(newPage);
    getSecrets(newPage, search);
  };

  useEffect(() => {
    if (value === 1) getSecrets(page, search);
  }, [value]);

  const ServiceModal = ({ open, service }) => {
    switch (service?.name) {
      case "PostgreSQL":
        return (
          <PostgreSQLConnectionForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      case "S3 Datalake":
        return (
          <S3DataLakeForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      case "OracleDB":
        return (
          <OracleDBConnectorForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      case "SQL Server":
        return (
          <SQLServerConnectorForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      case "Snowflake":
        return (
          <SnowflakeConnectorForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      case "ADLS Datalake":
        return (
          <ADLSDatalakeConnectorForm
            open={open}
            handleClose={handleClose}
            service={service}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{ style: { backgroundColor: "#47D7AC" } }}
        >
          <Tab label="Connectors" sx={{ fontWeight: "bold" }} />
          <Tab
            label={
              totalData > 0
                ? `Connections (${totalData})`
                : "Connections"
            }
            sx={{ fontWeight: "bold" }}
          />
        </Tabs>
      </Box>

      {value === 0 && (
        <Box sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} mb={3}>
            {filters.map((filter) => (
              <Chip
                key={filter}
                label={filter}
                color={
                  selectedFilters.includes(filter) ? "primary" : "default"
                }
                onClick={() => handleFilterClick(filter)}
              />
            ))}
          </Stack>

          <Row gutter={[16, 16]}>
            {filteredServices.map((service) => (
              <Col
                xs={24}
                sm={24}
                md={12}
                lg={6}
                xl={6}
                key={service.name}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    padding: "10px",
                    border: "1px solid #eee",
                    borderRadius: "10px",
                    "&:hover": {
                      cursor: "pointer",
                      borderColor: "#ddd",
                      boxShadow: "10px 5px 20px #ddd",
                    },
                  }}
                  onClick={() => handleCardClick(service)}
                >
                  <Avatar
                    src={service.icon}
                    variant="rounded"
                    style={{
                      filter: "contrast(150%)",
                      width: 56,
                      height: 56,
                    }}
                  />
                  <div
                    style={{
                      fontSize: "medium",
                      fontWeight: 500,
                      color: "#154360",
                      margin: "auto auto",
                    }}
                  >
                    {service.name}
                  </div>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      )}

      {value === 1 && (
        <Box sx={{ p: 3 }}>
          {secrets?.length ? (
            <SecretTable
              tableData={secrets}
              search={search}
              setSearch={setSearch}
              page={page}
              totalPages={Math.ceil(totalData / size)}
              onPageChange={handlePageChange}
            />
          ) : (
            !loading && (
              <Paper elevation={3} sx={{ height: "350px", mt: 2 }}>
                <ClientNoData
                  title={CONNECTOR_PAGE.connectorPageNoDataToDisplay}
                />
              </Paper>
            )
          )}
        </Box>
      )}

      {isOpen && (
        <ServiceModal open={isOpen} service={selectedService} />
      )}

      {loading && <LoadingIcon />}
    </Box>
  );
};

ConnectorsPage.propTypes = {
  open: PropTypes.bool,
  service: PropTypes.object,
};

export default ConnectorsPage;
